import { EnhancedFormModel, fetchRoute, findNestedProperty, FormModel, FormsFactory, getNestedProperty, ListModel, SummaryEnhancedFormModel, TAccountModel, TagsFactory, TEnhancedFormModel, TListModel, TUserModel } from "@cakemail-org/ui-components-v2"
import genericWrapper from "@cakemail-org/ui-components-v2/dist/esm/components/genericWrapper"
import { ERoutes } from "../../utils/url"
import { t } from "i18next"

export function buildFromScript({ form }: { form?: EnhancedFormModel | SummaryEnhancedFormModel }) {
    if (!form || !form.published_url) {
        return "Form not published yet. Please publish the form first."
    }

    return `
<script id="form-${form.id}">
(function() {
    // Change 'null' to match your container element ID if you want the form
    // to appear inside a specific element. 
    // If you leave this as null, the form will simply be inserted
    // right before this <script> tag in your page.
    var FORM_CONTAINER_ID = null; 
    
    var script = document.getElementById('form-${form.id}');
    function insertContent(content) {
        if (FORM_CONTAINER_ID) {
            var container = document.getElementById(FORM_CONTAINER_ID);
            if (container) {
                container.innerHTML += content;
                return;
            }
            console.error('Container "' + FORM_CONTAINER_ID + '" not found');
            return;
        }
        
        var div = document.createElement('div');
        div.innerHTML = content;
        (script && script.parentNode) ? script.parentNode.insertBefore(div, script) : document.body.appendChild(div);
    }
    
    fetch('${form.published_url}')
        .then(response => response.text())
        .then(html => {
            insertContent(html);
            if (script) script.remove();
        })
        .catch(error => {
            insertContent('<p style="color: #666;">Form could not be loaded. Please try again later.</p>');
            console.error('Error loading form:', error);
            if (script) script.remove();
        });
})();
</script>`
}

export function buildLink({ form, account, user }: { form: EnhancedFormModel | SummaryEnhancedFormModel, account: TAccountModel, user: TUserModel }) {
    return `${window.location.origin}/#${fetchRoute({ route: ERoutes.publicForm, params: { formId: form.id } })}?t=${form.published_url ? new URL(form.published_url).searchParams.get("token") : ""}&a=${account.id}&locale=${user.language}`
}


export function buildDefaultUrls({ list, account, user, form }: { list?: TListModel, account: TAccountModel, user: TUserModel, form?: TEnhancedFormModel }): { post_redirect_url: string, double_opt_in_redirect_url: string } {
    let queryParams = `?a=${account.id}&l=${form?.list_id}&locale=${user.language}&aName=${form?.name}`

    if (list) {
        queryParams += `&listName=${list.name}`
    }
    if (form?.id) {
        queryParams += `&f=${form.id}`
    }

    if (form?.double_opt_in) {
        queryParams += `&type=optin`
    } else {
        queryParams += `&type=doptin`
    }

    return {
        "post_redirect_url": window.location.origin + "/#" + fetchRoute({ route: ERoutes.listSubscription }) + queryParams,
        "double_opt_in_redirect_url": window.location.origin + "/#" + fetchRoute({ route: ERoutes.listConfirmation }) + queryParams
    }
}

export async function fetchListInfo({ listId }: { listId?: number }) {
    const list = listId ? new ListModel({ id: listId } as TListModel) : undefined
    async function fetchCustomAttributes() {
        if (list) {
            return list.getAttributes({ perPage: 200 }).then((attributes) => {
                return attributes.data
            }).catch((error) => {
                return []
            })
        }

        return []
    }
    async function fetchInterests() {
        if (list) {
            return list.getInterests({ perPage: 200 }).then((interests) => {
                return interests.data
            }).catch((error) => {
                return []
            })
        }
        return []
    }
    async function fetchTags() {
        return TagsFactory.list({ perPage: 200 }).then((tags) => {
            return tags.data
        }).catch((error) => {
            return []
        })
    }


    return Promise.all([
        fetchCustomAttributes(),
        fetchInterests(),
        fetchTags()
    ])
}

export async function updateFormLayoutOptions({ form, listId }: { form: TEnhancedFormModel, listId?: number }) {
    const [customAttributes, interests, tags] = await fetchListInfo({ listId })
    const jsonForm = findNestedProperty(form.content.json, "form")

    if (form) {

        jsonForm.structure.attributes.action = form.submission_url

        customAttributes.forEach((attribute) => {
            const fieldInfo = jsonForm.structure.fields[attribute.name]
            jsonForm.structure.fields[attribute.name] = {
                type: fieldInfo?.type || "text",
                attributes: {
                    ...fieldInfo?.attributes,
                    name: `custom_attributes[${attribute.name}]`,
                },
                label: fieldInfo?.label || attribute.name,
                removeFromLayout: fieldInfo?.removeFromLayout !== undefined ? fieldInfo.removeFromLayout : true,
                canBeRemovedFromLayout: fieldInfo?.canBeRemovedFromLayout !== undefined ? fieldInfo.canBeRemovedFromLayout : true,
                canBeModified: fieldInfo?.canBeModified !== undefined ? fieldInfo.canBeModified : true,
                id: fieldInfo?.id || attribute.name,
                options: fieldInfo?.options || null,
            }
        })

        const interestFieldInfo = jsonForm.structure.fields["interests"]
        jsonForm.structure.fields["interests"] = {
            type: interestFieldInfo?.type || "checkbox",
            attributes: {
                ...interestFieldInfo?.attributes,
                name: "interests[]",
            },
            label: t("general-interest_other"),
            removeFromLayout: interestFieldInfo?.removeFromLayout !== undefined ? interestFieldInfo.removeFromLayout : true,
            canBeRemovedFromLayout: interestFieldInfo?.canBeRemovedFromLayout !== undefined ? interestFieldInfo.canBeRemovedFromLayout : true,
            canBeModified: interestFieldInfo?.canBeModified !== undefined ? interestFieldInfo.canBeModified : true,
            id: interestFieldInfo?.id || "interests",
            options: interestFieldInfo?.options || interests.map((interest) => ({
                label: interest.alias || interest.name,
                value: interest.name,
            })),
        }

        const tagFieldInfo = jsonForm.structure.fields["tags"]
        jsonForm.structure.fields["tags"] = {
            type: tagFieldInfo?.type || "checkbox",
            attributes: {
                ...tagFieldInfo?.attributes,
                name: "tags[]",
            },
            label: t("general-tag_other"),
            removeFromLayout: tagFieldInfo?.removeFromLayout !== undefined ? tagFieldInfo.removeFromLayout : true,
            canBeRemovedFromLayout: tagFieldInfo?.canBeRemovedFromLayout !== undefined ? tagFieldInfo.canBeRemovedFromLayout : true,
            canBeModified: tagFieldInfo?.canBeModified !== undefined ? tagFieldInfo.canBeModified : true,
            id: tagFieldInfo?.id || "tags",
            options: tagFieldInfo?.options || tags.map((tag) => ({
                label: tag.tag,
                value: tag.tag,
            })),
        }

        jsonForm.structure.layout = []
        Object.keys(jsonForm.structure.fields).forEach((field) => {
            if (field === "email" || field === "submit_button") {
                return
            }
            jsonForm.structure.layout.push([field])
        })
        jsonForm.structure.layout.unshift(["email"])
        jsonForm.structure.layout.push(["submit_button"])


    }

    return form.content.json
}

/*
tags.forEach((ta) => {
    formBaseStructure.structure.fields[ta.tag + "-tags"] = {
        type: "hidden",
        label: ta.tag + " - hidden",

        removeFromLayout: true,
        canBeRemovedFromLayout: true,
    }
}),


*/